import { useEffect, useState } from "react";
import Header from "@/components/Header";
import ResultsStep from "@/components/ResultsStep";
import ReportDialog from "@/components/ReportDialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ClockCounterClockwise, Trash, ArrowLeft, FileText, User, PencilSimple, Check, X } from "@phosphor-icons/react";

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/comparisons");
      setItems(data);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const open = async (id) => {
    try {
      const { data } = await api.get(`/comparisons/${id}`);
      setSelected(data);
      setReport(data.report || null);
    } catch {
      toast.error("Failed to open comparison");
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/comparisons/${id}`);
      setItems(items.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const startEdit = (item, e) => {
    e.stopPropagation();
    setEditingId(item.id);
    setEditName(item.name || "");
  };

  const cancelEdit = (e) => {
    e?.stopPropagation();
    setEditingId(null);
    setEditName("");
  };

  const saveEdit = async (id, e) => {
    e?.stopPropagation();
    const name = editName.trim();
    if (!name) {
      toast.error("Name cannot be empty");
      return;
    }
    try {
      await api.patch(`/comparisons/${id}`, { name });
      setItems(items.map((i) => (i.id === id ? { ...i, name } : i)));
      setEditingId(null);
      setEditName("");
      toast.success("Renamed");
    } catch {
      toast.error("Rename failed");
    }
  };

  if (selected) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-6xl mx-auto px-6 py-10">
          <Button variant="ghost" onClick={() => setSelected(null)} className="gap-2 mb-6">
            <ArrowLeft size={18} /> Back to history
          </Button>
          <ResultsStep
            result={selected.result}
            comparisonId={selected.id}
            name={selected.name}
            onBack={() => setSelected(null)}
            onReport={() => setReportOpen(true)}
          />
        </main>
        <ReportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          comparisonId={selected.id}
          report={report}
          setReport={setReport}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-8">
          <ClockCounterClockwise size={26} className="text-primary" />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Comparison history</h2>
        </div>

        {loading ? (
          <div className="font-mono text-sm text-muted-foreground animate-pulse">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center">
            <FileText size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No comparisons yet. Run one from the workspace.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="history-list">
            {items.map((item) => (
              <div
                key={item.id}
                data-testid={`history-item-${item.id}`}
                onClick={() => open(item.id)}
                className="border border-border bg-white p-4 flex items-center justify-between cursor-pointer hover:border-primary transition-colors group"
              >
                <div>
                  {editingId === item.id ? (
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        data-testid={`history-rename-input-${item.id}`}
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit(item.id, e);
                          if (e.key === "Escape") cancelEdit(e);
                        }}
                        className="border border-primary bg-white px-2 py-1 text-sm font-semibold outline-none focus:ring-1 focus:ring-primary w-72 max-w-full"
                      />
                      <button
                        data-testid={`history-rename-save-${item.id}`}
                        onClick={(e) => saveEdit(item.id, e)}
                        className="text-primary hover:opacity-70 transition-opacity p-1"
                      >
                        <Check size={16} weight="bold" />
                      </button>
                      <button
                        data-testid={`history-rename-cancel-${item.id}`}
                        onClick={cancelEdit}
                        className="text-muted-foreground hover:text-destructive transition-colors p-1"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-semibold group-hover:text-primary transition-colors">{item.name}</span>
                      <button
                        data-testid={`history-rename-${item.id}`}
                        onClick={(e) => startEdit(item, e)}
                        className="text-muted-foreground hover:text-primary transition-colors p-1 opacity-0 group-hover:opacity-100"
                        title="Rename"
                      >
                        <PencilSimple size={14} />
                      </button>
                    </div>
                  )}
                  <div className="font-mono text-xs text-muted-foreground mt-1">
                    {new Date(item.created_at).toLocaleString()} ·{" "}
                    {item.result?.summary?.length || 0} comparisons
                  </div>
                  {item.created_by_name && (
                    <div
                      data-testid={`history-author-${item.id}`}
                      className="flex items-center gap-1 font-mono text-xs text-muted-foreground mt-1"
                    >
                      <User size={12} /> {item.created_by_name}
                    </div>
                  )}
                </div>
                <button
                  data-testid={`history-delete-${item.id}`}
                  onClick={(e) => remove(item.id, e)}
                  className="text-muted-foreground hover:text-destructive transition-colors p-2"
                >
                  <Trash size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
